import {
  LOGO_B64,
  SIG_B64,
  COMPANY,
  C_DARK,
  C_GREY_TEXT,
  C_GREY_BG,
  C_WHITE,
} from "./generateInspectionPDF";

const C_NAVY = [30, 58, 95];

async function fetchDataUrl(url) {
  const resp = await fetch(url);
  const blob = await resp.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function generateGeneralQuotePDF(quote, opts = {}) {
  const { jsPDF } = window.jspdf;

  // Preload all item photos and image attachments as data URLs before rendering
  const photoCache = {};
  for (const item of (quote.items || [])) {
    for (const ph of (item.photos || [])) {
      if (ph.photo_url && !photoCache[ph.photo_url]) {
        try { photoCache[ph.photo_url] = await fetchDataUrl(ph.photo_url); }
        catch (e) { /* skip failed photos */ }
      }
    }
  }

  // Preload all attachments — try to fetch regardless of type; embedding will fail gracefully for non-images
  const attachmentCache = {};
  for (const att of (quote.attachments || [])) {
    if (att.file_url) {
      try { attachmentCache[att.id] = await fetchDataUrl(att.file_url); }
      catch (e) { console.warn("Attachment fetch failed:", att.name, e); }
    }
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210;
  const pageH = 297;
  const margin = 15;
  const contentW = pageW - margin * 2;
  let y = margin;
  let pageNum = 1;

  const fmt2 = (n) =>
    Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  function addPage() {
    doc.addPage();
    pageNum++;
    y = margin;
    drawPageFooter();
  }

  function drawPageFooter() {
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text(`Page ${pageNum}`, pageW / 2, pageH - 7, { align: "center" });
  }

  function checkBreak(needed) {
    if (y + needed > pageH - 18) addPage();
  }

  // ── HEADER ───────────────────────────────────────────────────────────────
  const logoW = 38;
  const logoH = logoW * (211 / 500);
  doc.addImage("data:image/jpeg;base64," + LOGO_B64, "JPEG", margin, y, logoW, logoH);

  const cx = pageW - margin;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_DARK);
  doc.text(COMPANY.name, cx, y + 3, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(COMPANY.accreditation, cx, y + 7.5, { align: "right" });
  doc.text(COMPANY.address, cx, y + 11.5, { align: "right" });
  doc.text(COMPANY.phone, cx, y + 15.5, { align: "right" });
  doc.text(COMPANY.email, cx, y + 19.5, { align: "right" });

  y += logoH + 7;
  doc.setDrawColor(...C_GREY_TEXT);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // ── TITLE BLOCK ──────────────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_NAVY);
  doc.text("Quotation", pageW / 2, y, { align: "center" });
  y += 5;

  if (quote.title) {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_DARK);
    doc.text(quote.title, pageW / 2, y, { align: "center" });
    y += 5;
  }

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(`Reference: GQ-${String(quote.id).padStart(3, "0")}`, pageW / 2, y, { align: "center" });
  y += 7;

  // ── DETAILS BOX ──────────────────────────────────────────────────────────
  doc.setFillColor(...C_GREY_BG);
  doc.roundedRect(margin, y, contentW, 19, 2, 2, "F");
  const col1 = margin + 3;
  const col2 = margin + contentW / 2 + 3;
  const labelH = 4;

  const detailRows = [
    ["Site Name", quote.site_name || "", "Quote Date", new Date(quote.created_at).toLocaleDateString("en-AU")],
    ["Site Address", quote.site_address || "", "", ""],
  ];

  let dy = y + 5;
  detailRows.forEach(([l1, v1, l2, v2]) => {
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text(l1.toUpperCase(), col1, dy);
    doc.text(l2.toUpperCase(), col2, dy);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_DARK);
    doc.setFontSize(8.5);
    doc.text(String(v1), col1, dy + labelH);
    doc.text(String(v2), col2, dy + labelH);
    dy += 9;
  });
  y += 23;

  // ── TABLE HEADER ─────────────────────────────────────────────────────────
  checkBreak(12);
  doc.setFillColor(...C_NAVY);
  doc.rect(margin, y, contentW, 7, "F");
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_WHITE);

  // Column positions (left edge) and widths — total must fit contentW (180mm)
  // # 6 | Desc 48 | Solution 38 | Qty 10 | Unit Price 24 | GST 22 | Total 27 | padding gaps handled by right-align
  const colNum   = margin + 2;
  const colDesc  = margin + 8;
  const colSol   = margin + 58;
  const colQty   = margin + 98;
  const colUnit  = margin + 110;
  const colGst   = margin + 136;
  const colTotal = pageW - margin - 2;  // right-aligned

  doc.text("#",                  colNum,   y + 4.5);
  doc.text("Description",        colDesc,  y + 4.5);
  doc.text("Proposed Solution",  colSol,   y + 4.5);
  doc.text("Qty",                colQty,   y + 4.5, { align: "right" });
  doc.text("Unit Price",         colUnit + 20, y + 4.5, { align: "right" });
  doc.text("GST",                colGst + 19,  y + 4.5, { align: "right" });
  doc.text("Total (inc. GST)",   colTotal, y + 4.5, { align: "right" });
  y += 9;

  // ── TABLE ROWS ────────────────────────────────────────────────────────────
  const items = quote.items || [];
  const descW     = 48;
  const solutionW = 38;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const descLines     = doc.splitTextToSize(item.description || "",     descW - 2);
    const solutionLines = doc.splitTextToSize(item.proposed_solution || "", solutionW - 2);
    const lineH   = 4.5;
    const maxLines = Math.max(descLines.length, solutionLines.length);
    const itemPhotos = (item.photos || []).map(ph => photoCache[ph.photo_url]).filter(Boolean);
    const photoStripH = itemPhotos.length > 0 ? 32 : 0;
    const rowH    = maxLines * lineH + 3 + photoStripH;

    checkBreak(rowH + 4);

    if (i % 2 === 0) {
      doc.setFillColor(250, 250, 252);
      doc.rect(margin, y, contentW, rowH, "F");
    }

    const qty       = parseFloat(item.quantity) || 1;
    const unitPrice = parseFloat(item.unit_price_ex_gst) || 0;
    const lineEx    = qty * unitPrice;
    const lineGst   = lineEx * 0.1;
    const lineInc   = lineEx * 1.1;

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_DARK);
    doc.text(String(i + 1), colNum, y + lineH);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(descLines,     colDesc, y + lineH);
    doc.text(solutionLines, colSol,  y + lineH);

    doc.setTextColor(...C_DARK);
    doc.text(
      Number(qty).toLocaleString("en-AU", { maximumFractionDigits: 2 }),
      colQty, y + lineH, { align: "right" }
    );
    doc.text(`$${fmt2(unitPrice)}`, colUnit + 20, y + lineH, { align: "right" });

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_NAVY);
    doc.text(`$${fmt2(lineGst)}`, colGst + 19, y + lineH, { align: "right" });
    doc.text(`$${fmt2(lineInc)}`, colTotal,     y + lineH, { align: "right" });

    // Embed item photos as a strip below the text content
    if (itemPhotos.length > 0) {
      const photoW = 28;
      const photoH = 28;
      const photoY = y + maxLines * lineH + 4;
      itemPhotos.forEach((dataUrl, pi) => {
        const photoX = colDesc + pi * (photoW + 2);
        try { doc.addImage(dataUrl, "JPEG", photoX, photoY, photoW, photoH); }
        catch (e) { /* skip corrupt/unsupported image */ }
      });
    }

    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(margin, y + rowH, margin + contentW, y + rowH);
    y += rowH;
  }

  // ── TOTALS ────────────────────────────────────────────────────────────────
  checkBreak(32);
  y += 6;
  const totW = 80;
  const totX = pageW - margin - totW;

  const totalExGst  = Number(quote.total_ex_gst)  || 0;
  const gst         = Number(quote.gst)            || 0;
  const totalIncGst = Number(quote.total_inc_gst)  || 0;

  const totRows = [
    { label: "Subtotal (ex. GST)", value: `$${fmt2(totalExGst)}`, bold: false },
    { label: "GST (10%)",          value: `$${fmt2(gst)}`,        bold: false },
    { label: "TOTAL (inc. GST)",   value: `$${fmt2(totalIncGst)}`, bold: true },
  ];

  totRows.forEach(({ label, value, bold }) => {
    checkBreak(8);
    if (bold) {
      doc.setFillColor(...C_NAVY);
      doc.rect(totX - 2, y - 4, totW + 4, 8, "F");
      doc.setTextColor(...C_WHITE);
    } else {
      doc.setTextColor(...C_DARK);
    }
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 9 : 8);
    doc.text(label, totX, y);
    doc.text(value, pageW - margin, y, { align: "right" });
    y += 8;
  });

  y += 2;
  doc.setFontSize(7);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("All amounts are in Australian Dollars. GST is calculated at 10%.", margin, y);
  y += 8;

  // ── SIGNATURE ─────────────────────────────────────────────────────────────
  checkBreak(52);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + contentW, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_DARK);
  doc.text("Authorised By", margin, y);
  y += 7;

  const sigW   = (contentW - 10) / 3;
  const sx0    = margin;
  const sx1    = margin + sigW + 5;
  const sx2    = margin + (sigW + 5) * 2;
  const dateStr = new Date().toLocaleDateString("en-AU");

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("AUTHORISED SIGNATORY", sx0, y);
  doc.text("DATE",                  sx1, y);
  doc.text("SIGNATURE",             sx2, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...C_DARK);
  doc.text(COMPANY.signatory || "", sx0, y + 5);
  doc.text(dateStr,                 sx1, y + 5);

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.line(sx0, y + 6, sx0 + sigW, y + 6);
  doc.line(sx1, y + 6, sx1 + sigW, y + 6);

  const sigImgW = sigW;
  const sigImgH = sigImgW * (211 / 500);
  try {
    doc.addImage("data:image/png;base64," + SIG_B64, "PNG", sx2 + 5, y - sigImgH + 8, sigImgW, sigImgH);
  } catch (e) {}
  doc.line(sx2, y + 6, sx2 + sigW, y + 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(COMPANY.name,        sx0, y + 11);
  doc.text(COMPANY.accreditation, sx0, y + 15);

  drawPageFooter();

  // ── ATTACHMENTS APPENDIX ──────────────────────────────────────────────────
  const attachments = quote.attachments || [];
  if (attachments.length > 0) {
    doc.addPage();
    pageNum++;
    y = margin;
    drawPageFooter();

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_NAVY);
    doc.text("Attachments", margin, y);
    y += 8;

    doc.setDrawColor(...C_GREY_TEXT);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageW - margin, y);
    y += 6;

    let attNum = 0;
    for (const att of attachments) {
      attNum++;
      const dataUrl = attachmentCache[att.id];
      let embedded = false;

      if (dataUrl) {
        // Try to embed as image — works for JPG, PNG, GIF, WebP etc.
        // Falls back to text listing for PDFs/Office docs (addImage throws for those)
        if (y > pageH - 50) { doc.addPage(); pageNum++; y = margin; drawPageFooter(); }

        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...C_GREY_TEXT);
        doc.text(`Attachment ${attNum}: ${att.name}`, margin, y);
        y += 5;

        try {
          const img = new Image();
          await new Promise((res) => { img.onload = res; img.onerror = res; img.src = dataUrl; });
          const aspect = img.naturalHeight / (img.naturalWidth || 1);
          const imgW = contentW;
          const imgH = Math.min(imgW * aspect, 200);
          if (y + imgH > pageH - 18) { doc.addPage(); pageNum++; y = margin; drawPageFooter(); }
          const mimeMatch = dataUrl.match(/^data:image\/(\w+);/);
          const fmt = mimeMatch ? mimeMatch[1].toUpperCase().replace("JPG", "JPEG") : "JPEG";
          doc.addImage(dataUrl, fmt, margin, y, imgW, imgH);
          y += imgH + 10;
          embedded = true;
        } catch (e) {
          // Not an embeddable image — fall through to text listing below
          y -= 5; // undo the label advance; we'll re-draw in the text block
        }
      }

      if (!embedded) {
        // Non-image attachment (PDF, Office doc, etc.) — list by filename
        if (y > pageH - 25) { doc.addPage(); pageNum++; y = margin; drawPageFooter(); }
        doc.setFillColor(248, 249, 251);
        doc.roundedRect(margin, y - 3, contentW, 10, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(...C_DARK);
        doc.text(`${attNum}.`, margin + 3, y + 4);
        doc.setFont("helvetica", "normal");
        doc.text(att.name, margin + 9, y + 4);
        if (att.file_size) {
          const sz = att.file_size > 1048576
            ? (att.file_size / 1048576).toFixed(1) + " MB"
            : Math.round(att.file_size / 1024) + " KB";
          doc.setTextColor(...C_GREY_TEXT);
          doc.text(sz, pageW - margin - 2, y + 4, { align: "right" });
        }
        y += 13;
      }
    }
  }

  const siteName = (quote.site_name || "site").replace(/[^a-z0-9]/gi, "_");
  if (!opts.noDownload) {
    doc.save(`GQ-${String(quote.id).padStart(3, "0")}_${siteName}_Quotation.pdf`);
  }

  return doc.output("blob");
}
